// src/theme.js
// Faana Product Voice: "Clear, precise, human. Non-performative."
// Canvas from autumnmanning.com, Inter-only from corporate, ink + teal palette.
import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#141B2D',      // Ink — AppBar, sidebar, primary dark
      light: '#2D3446',     // Hover states on dark surfaces
      dark: '#0C1220',      // Pressed states
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#23A38F',      // Faana teal — CTA buttons, active states
      light: '#4DB8A8',     // Hover on contained buttons
      dark: '#1A7A6B',      // Pressed state
      contrastText: '#ffffff',
    },
    accent: {
      teal: '#23A38F',
      gold: '#D8A200',
      blue: '#00BCD4',      // Links, informational highlights
    },
    grey: {
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#8E95A5',       // Mist
      400: '#78818F',
      500: '#5C6478',       // Stone
      600: '#4A5060',
      700: '#3A3F4C',
      800: '#2D3446',
      900: '#141B2D',
    },
    background: {
      default: '#FAFAF8',   // Warm white canvas
      paper: '#FFFFFF',      // Cards, elevated surfaces
    },
    text: {
      primary: '#141B2D',    // Ink, not pure black
      secondary: '#5C6478',  // Stone secondary
      disabled: '#8E95A5',   // Mist meta text
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      'sans-serif',
    ].join(','),
    h1: {
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
    },
    h4: {
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
    },
    h5: {
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
    },
    subtitle1: {
      fontFamily: 'Inter, sans-serif',
    },
    subtitle2: {
      fontFamily: 'Inter, sans-serif',
    },
    body1: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '0.875rem', // 14px for data density
    },
    body2: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '0.8125rem', // 13px
    },
    button: {
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
      textTransform: 'none',
    },
    caption: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '0.75rem',
    },
    overline: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '0.6875rem',
      letterSpacing: '0.08em',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#141B2D',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8,
          textTransform: 'none',
        },
        containedPrimary: {
          backgroundColor: '#23A38F',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#1A7A6B',
          },
        },
        outlinedPrimary: {
          borderColor: '#23A38F',
          color: '#23A38F',
          '&:hover': {
            backgroundColor: 'rgba(35, 163, 143, 0.04)',
            borderColor: '#1A7A6B',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#141B2D',
          color: '#ffffff',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
          // Override for menu items (dropdowns on light bg)
          '.MuiMenuItem-root &': {
            color: '#5C6478',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: '#ffffff',
          // Override for menu items (dropdowns on light bg)
          '.MuiMenuItem-root &': {
            color: '#141B2D',
          },
          // Override for list items inside Paper (light bg like chat sidebar)
          // Exclude drawer paper which has a dark background
          '.MuiPaper-root:not(.MuiDrawer-paper) &': {
            color: '#141B2D',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          '&.Mui-selected': {
            color: '#23A38F',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          color: '#141B2D',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#141B2D',
          '&:hover': {
            backgroundColor: 'rgba(20, 27, 45, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(35, 163, 143, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(35, 163, 143, 0.12)',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          color: '#141B2D',
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1', h2: 'h2', h3: 'h3', h4: 'h4', h5: 'h5', h6: 'h6',
          subtitle1: 'h6', subtitle2: 'h6',
          body1: 'p', body2: 'p',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#23A38F',
        },
      },
    },
  },
});

// Load Inter font from Google Fonts
if (typeof document !== 'undefined') {
  const link = document.createElement('link');
  link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
  link.rel = 'stylesheet';
  document.head.appendChild(link);
}

export default theme;
